import get from 'lodash/get';
import { QUERY_FOR_PHONE_ONLY } from 'web-ui-kit/media';
import { MESSAGE_TYPES } from 'web-ui-kit/messenger-consts';
import { SKELETON_LOADER_SRC } from 'web-ui-kit/images/ImageWithSkeletonLoader';
import { reduceMembersToDict } from 'redux/models/Messenger/Conversation';
import Message from 'redux/models/Messenger/Message';
import * as consts from './consts';

import {
  navigateToStudioSpaceAndDesignFor
} from 'services/routing';

/**
 * Determines whether this automated message needs a text response
 *
 * This is used by actions#sendMessage to determine whether the send should go
 * as a response to the automated message or be sent as a new message
 */
export const autoMessageNeedsTextResponse = ({ activeMessageFlow }) => {
  const activeMessage = activeMessageFlow.messages[activeMessageFlow.messages.length - 1];
  return activeMessage.messageType === MESSAGE_TYPES.textResponse &&
    (!activeMessage.response || activeMessage.response.length < 1);
};

/**
 * Finds a message that contains localized times
 */
export const findAutoMessageWithHandleBarTimes = (messages) => {
  return messages.find(message =>
    message.userId === '-1' &&
    get(message, 'message', '').search(/{{(localizedDate|localizedTime) [0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}Z}}/g) >= 0);
};

/**
 * Finds a message in the cache with the given message flow name if it exists
 * @returns Message array with found results or null
 */
export const findActiveCalendlyScheduleLinkMessages = (messages) => {
  return messages.filter(message =>
    message.action === 'link' &&
    message.messageType === MESSAGE_TYPES.buttonWithLink &&
    get(message, 'subComponentProps.option.value', '').includes('https://calendly.com')
  );
};

/**
 * Gets the active automated message flow message from state
 * the active auto message is defined as:
 *
 * An automated message that is the last (most recent) message in the message list
 */
export const getActiveAutomatedMessageFromState = ({ spaceId, messageId, state }) => {
  const activeMessage = state.messages[spaceId].messages.find(
    message => message.messageId === messageId);
  const isCompleteFlow = get(activeMessage, 'messageFlow.complete', false);
  return activeMessage && activeMessage.messageFlow && !isCompleteFlow ? activeMessage : null;
};

const generateTmpMessageId = () => {
  return `${Date.now()}--${(Math.random() * 1000).toString(36).substr(3, 9)}`;
};

/**
 * Generates the temporary messages generated on message send
 * used to populate the messenger before the server finishes processing
 * @param message - the message text for the sent message
 * @param images - the images being sent with the message
 */
export const generateTmpMessages = ({
  error,
  message,
  images,
  spaceId,
  userId,
  keepImageData = false,
  useImageUrls = false
}) => {
  const messages = [];
  if (images && images.length > 0) {
    // Generate an images message
    const messageImages = images.map(image => (
      useImageUrls ? image.preview : SKELETON_LOADER_SRC
    ));
    messages.push(new Message({
      error,
      images: messageImages,
      imageData: keepImageData ? images : null,
      message,
      messageId: generateTmpMessageId(),
      spaceId,
      userId
    }));
  } else if (message) {
    // Generate a text message
    messages.push(new Message({
      error,
      message,
      messageId: generateTmpMessageId(),
      spaceId,
      userId
    }));
  }
  const messageIds = messages.map(message => message.messageId);
  return { messages, messageIds };
};

/**
 * Allows retrieval of the automated message response value based on the action type
 */
export const getResponseForType = ({ type, payload }) => {
  switch (type) {
    case consts.MESSENGER_ACTION_DESIGN_RATED:
      return [payload.value.toString()];
    case consts.MESSENGER_SUBMITTED_MULTI_SELECT:
      return payload.selections.map(selection => selection.value);
    case consts.MESSENGER_ACTION_IMAGE_SELECTED:
      return [payload.selection.value];
    case consts.MESSENGER_ACTION_SELECT_LAYOUT:
    case consts.MESSENGER_SEND_MESSAGE:
      return [payload.value];
    default:
      return undefined;
  }
};

/**
 * Determines whether we should send 'complete_flow' to the server to finish up the
 * automated flow
 */
const getShouldCompleteFlow = ({ messageFlow, messageFlowMessage }) => {
  return messageFlow.name === consts.REDESIGN_REQUEST_FLOW_NAME &&
    messageFlowMessage.messageType === MESSAGE_TYPES.textResponse;
};

/**
 * Parses a set of breadcrumbs for a space link and navigates to the destination
 */
export const parseSpaceLink = ({ breadcrumbs, closeMessenger }) => {
  // Space and Design structure is space/{spaceId}/design/{designid}
  // Layout structure is space/{spaceId}/layouts
  const spaceId = get(breadcrumbs, '[1]');
  const secondaryResourceType = get(breadcrumbs, '[2]');
  const designId = get(breadcrumbs, '[3]');
  switch (secondaryResourceType) {
    case 'design':
      navigateToStudioSpaceAndDesignFor({
        spaceId: spaceId,
        designId: designId,
        replace: false,
        doNotScrollTop: false
      });
      break;
    case 'layouts':
      // If this is a mobile link to layouts we want to close
      // the messenger with the link
      if (window.matchMedia(QUERY_FOR_PHONE_ONLY).matches) {
        closeMessenger();
      }
      // In web-post-purchase app, Messenger is only on Studio page.
      // This means that if we get here then we are already in Studio
      // and we do not need to perform any further actions.
      break;
    default:
      break;
  }
};

/**
 * Reducer used by useMessages to create a dict for conversation lookup
 */
export const reduceConversationsToDictById = (prev, conversation) => {
  const row = {
    [conversation.spaceId]: {
      ...conversation,
      members: conversation.members.reduce(reduceMembersToDict, {})
    }
  };
  return { ...prev, ...row };
};

/**
 * Reducer used by useMessage to add messages to the dict for
 * each spaceId
 */
export const reduceMessagesToDictBySpaceId = () => (prev, message) => {
  const row = { [message.spaceId]: message };
  return { ...prev, ...row };
};

/**
 * Reducer used by useMessages to create a dict for name lookup
 */
export const reduceProjectsToDictBySpaceId = (prev, project) => {
  const row = { [project.id()]: project };
  return { ...prev, ...row };
};

/**
 * Generates a new messageFlow messages set with the response added
 * to the index
 * @returns new messageFlow messages array
 */
export const replaceAutoMessageResponse = ({ message, index, response }) => {
  return {
    ...message,
    messageFlow: {
      ...message.messageFlow,
      messages: [
        ...message.messageFlow.messages.slice(0, index),
        {
          ...message.messageFlow.messages[index],
          response
        },
        ...message.messageFlow.messages.slice(index + 1)
      ]
    }
  };
};

/**
 * Loops through the values in state for multi-select
 * and updates all the related responses
 * @param stateValues dictionary of "index": [{values}]
 * @param message the message to update
 */
export const updateAllMultiSelectValues = ({
  stateValues,
  message
}) => {
  let updatedMessage = { ...message };
  if (stateValues) {
    for (const messageIndex of Object.keys(stateValues)) {
      updatedMessage = replaceAutoMessageResponse({
        message: updatedMessage,
        index: parseInt(messageIndex, 10),
        response: stateValues[messageIndex].map(selection => selection.value)
      });
    }
  }
  return updatedMessage;
};

/**
 * Updates an automated message response with a text response
 *
 * @param activeMessageFlowMessage: Message that we want to replace the response in
 * @param dispatch: state dispatch for redux/Messenger/reducer
 * @param updateMessage: action to use for the dispatch
 * @param message: the text response to fill the automated message response
 */
export const updateAutomatedMessageWithTextResponse = ({
  activeMessageFlowMessage,
  dispatch,
  updateMessage,
  message
}) => {
  // Retrieve the active message flow
  const activeMessageFlow = activeMessageFlowMessage.messageFlow;
  // Get the last message in the flow
  const activeMessage = activeMessageFlow.messages[activeMessageFlow.messages.length - 1];
  if (activeMessage.messageType === MESSAGE_TYPES.textResponse &&
    (!activeMessage.response || activeMessage.response.length < 1)) {
    const completeFlow = getShouldCompleteFlow({
      messageFlow: activeMessageFlow,
      messageFlowMessage: activeMessage
    });

    dispatch(updateMessage({
      type: consts.MESSENGER_SEND_MESSAGE,
      payload: {
        messageId: activeMessageFlowMessage.messageId,
        messageIndex: activeMessageFlow.messages.length - 1,
        completeFlow,
        value: message
      }
    }));
  }
};
